import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilmCard from "@/components/feature/FilmCard";
import FilterPanel, { type FilmFilters } from "@/pages/films/components/FilterPanel";
import Pagination from "@/pages/films/components/Pagination";
import { films } from "@/mocks/films";
import type { Film } from "@/types/content";

const PAGE_SIZE = 9;

type SortKey = "newest" | "rating" | "popular";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "newest", label: "最新上映" },
  { key: "rating", label: "评分高低" },
  { key: "popular", label: "热门程度" },
];

const emptyFilters: FilmFilters = { genres: [], years: [], ratings: [], tags: [] };

const ratingThreshold: Record<string, number> = {
  "9.0 分以上": 9,
  "8.5 分以上": 8.5,
  "8.0 分以上": 8,
};

export default function Films() {
  usePageMeta(
    "球幕影院-影片分类-最新特色影片一览",
    "浏览球幕影院特色影片分类，支持按类型、年份、评分与特色标签筛选排序，快速找到理想的沉浸式观影选择。",
    "球幕影院影片,影片分类,沉浸式电影,特色影片,球幕技术",
  );
  const [searchParams] = useSearchParams();
  const initialSort = (searchParams.get("sort") as SortKey) || "newest";
  const initialTag = searchParams.get("tag");
  const initialQuery = searchParams.get("q") || "";

  const [filters, setFilters] = useState<FilmFilters>({
    ...emptyFilters,
    tags: initialTag ? [initialTag] : [],
  });
  const [sort, setSort] = useState<SortKey>(
    sortOptions.some((o) => o.key === initialSort) ? initialSort : "newest",
  );
  const [keyword, setKeyword] = useState(initialQuery);
  const [page, setPage] = useState(1);

  const allGenres = useMemo(() => {
    const set = new Set<string>();
    (films as Film[]).forEach((film) => film.genres.forEach((g) => set.add(g)));
    return Array.from(set);
  }, []);

  const allYears = useMemo(() => {
    const set = new Set<number>();
    (films as Film[]).forEach((film) => set.add(film.year));
    return Array.from(set).sort((a, b) => b - a);
  }, []);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    (films as Film[]).forEach((film) => film.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, []);

  const handleToggle = (group: keyof FilmFilters, value: string | number) => {
    setFilters((prev) => {
      const list = prev[group] as (string | number)[];
      const exists = list.includes(value);
      const next = exists ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [group]: next } as FilmFilters;
    });
  };

  const handleClear = () => {
    setFilters(emptyFilters);
    setKeyword("");
    setPage(1);
  };

  const activeCount =
    filters.genres.length + filters.years.length + filters.ratings.length + filters.tags.length;

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const result = (films as Film[]).filter((film) => {
      if (q && !film.title.toLowerCase().includes(q) && !film.director.toLowerCase().includes(q)) {
        return false;
      }
      if (filters.genres.length && !filters.genres.some((g) => film.genres.includes(g))) return false;
      if (filters.years.length && !filters.years.includes(film.year)) return false;
      if (filters.tags.length && !filters.tags.some((t) => film.tags.includes(t))) return false;
      if (filters.ratings.length) {
        const pass = filters.ratings.some((r) => film.rating >= (ratingThreshold[r] ?? 0));
        if (!pass) return false;
      }
      return true;
    });

    result.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "popular") return b.popularity - a.popularity;
      return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
    });
    return result;
  }, [filters, keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filters, keyword, sort]);

  return (
    <div className="w-full pt-20">
      <section className="bg-background-100/60 border-b border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">影片分类</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4">
            球幕影院 · 影片分类
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            汇集为穹顶空间打造的特色影片，涵盖科幻、纪录、剧情等类型。使用筛选与排序，
            快速找到契合你口味的沉浸式观影选择。
          </p>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-8">
          <FilterPanel
            genreOptions={allGenres}
            yearOptions={allYears}
            tagOptions={allTags}
            filters={filters}
            onToggle={handleToggle}
            onClear={handleClear}
            activeCount={activeCount}
          />

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="relative w-full sm:max-w-xs">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影片标题或导演"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 font-label whitespace-nowrap">排序</span>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                        sort === option.key
                          ? "bg-accent-500 text-background-50"
                          : "text-foreground-700 hover:text-foreground-950"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p className="text-xs text-foreground-500 mb-5">
              共找到 {filtered.length} 部影片
              {currentPage > 1 && ` · 第 ${currentPage} / ${totalPages} 页`}
            </p>

            {paged.length === 0 ? (
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-12 text-center">
                <i className="ri-movie-line text-4xl text-foreground-500" />
                <h2 className="font-heading font-semibold text-foreground-950 mt-4 mb-2">
                  没有找到匹配的影片
                </h2>
                <p className="text-sm text-foreground-600 mb-6">
                  试试调整筛选条件，或清空全部筛选重新浏览
                </p>
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-label hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  清空筛选
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {paged.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
            )}

            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </section>
    </div>
  );
}